import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  IconButton,
  Alert
} from '@mui/material';
import {
  Security,
  Speed,
  CloudOff,
  Router,
  CameraAlt,
  Warning,
  Pets,
  LocalFlorist,
  Shield,
  Memory,
  DeviceHub,
  NetworkCheck,
  CheckCircle,
  ArrowForward,
  Launch,
  Login as LoginIcon,
  Psychology,
  Menu as MenuIcon,
  Download
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authPath } from '../utils/authRedirect';
import PublicFooter from './PublicFooter';

/** Match the Nano Banana aspectRatio used for each still. Do not use fixed height + objectFit cover. */
const HomepageArt = ({ src, alt, shadow = 8, aspectRatio = '16 / 9' }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: theme.shadows[shadow],
        bgcolor: '#020617',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          height: 'auto',
          aspectRatio,
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </Box>
  );
};

const Homepage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const loginHref = authPath('/login', location.search);
  const registerHref = authPath('/register', location.search);
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState(null);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleMobileMenuClose();
  };

      const useCases = [
    {
      title: "Wildlife Deterrent",
      description: "Protect your garden from deer, raccoons, and other wildlife with automated deterrents",
      icon: <Pets sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      features: ["AI-powered detection", "Adaptive ML fine-tuning", "Sprinkler activation", "Audio deterrents", "Species identification"]
    },
    {
      title: "Wildfire Detection",
      status: "In development",
      description: "Early smoke and fire detection is in development. It is not part of the deer model that ships today.",
      icon: <Warning sx={{ fontSize: 40, color: theme.palette.error.main }} />,
      features: ["Not in the current deer model", "Shown here so the roadmap is visible"]
    },
    {
      title: "Security Monitoring",
      description: "Advanced perimeter security with intelligent threat detection and response",
      icon: <Security sx={{ fontSize: 40, color: theme.palette.success.main }} />,
      features: ["Person detection", "Vehicle identification", "Intrusion alerts", "Automated logging"]
    },
    {
      title: "Garden Protection",
      description: "Keep your plants safe with precision watering and pest deterrents",
      icon: <LocalFlorist sx={{ fontSize: 40, color: theme.palette.secondary.main }} />,
      features: ["Plant health monitoring", "Irrigation control", "Pest detection", "Growth tracking"]
    }
  ];

  const gatewayFeatures = [
    {
      title: "Edge AI Processing",
      description: "High-performance computing platforms deliver exceptional AI processing power",
      icon: <Memory />
    },
    {
      title: "You install it",
      description: "Do-it-yourself software on a PC you supply. The USB installer erases the target drive. It is not a five-minute plug-in appliance.",
      icon: <DeviceHub />
    },
    {
      title: "Zero Trust Security",
      description: "WireGuard VPN with outbound-only connections ensures maximum security",
      icon: <Shield />
    },
    {
      title: "Offline Operation",
      description: "Core AI processing works without internet - your data stays private and local",
      icon: <CloudOff />
    },
    {
      title: "Multi-Camera Support",
      description: "Connect multiple RTSP cameras for comprehensive coverage",
      icon: <CameraAlt />
    },
    {
      title: "Real-time Processing",
      description: "Timely detection and response for critical scenarios",
      icon: <Speed />
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Top Navigation Bar */}
      <AppBar position="fixed" sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box
              component="img"
              src="/eyesense-logo-v2.png"
              alt="EyeSense.ai Logo"
              sx={{ height: 54, width: 54, mr: 1.6, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              EyeSense.ai
            </Typography>
          </Box>
          {isAuthenticated ? (
            <>
              <Button
                color="primary"
                onClick={() => navigate('/dashboard')}
                sx={{ mr: 1 }}
              >
                Dashboard
              </Button>
              <Button
                color="primary"
                onClick={() => navigate('/gateways')}
                sx={{ mr: 1 }}
              >
                Gateways
              </Button>
              <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
                Welcome, {user?.username || user?.email}
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                <Button
                  color="primary"
                  onClick={() => navigate('/install-gateway')}
                  sx={{ mr: 1 }}
                >
                  Install Gateway
                </Button>
                <Button
                  color="primary"
                  onClick={() => navigate('/subscription/plans')}
                  sx={{ mr: 1 }}
                >
                  Pricing
                </Button>
                <Button
                  color="primary"
                  startIcon={<LoginIcon />}
                  onClick={() => navigate(loginHref)}
                  sx={{ mr: 1 }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate(registerHref)}
                >
                  Get Started
                </Button>
              </Box>
              <IconButton
                color="primary"
                onClick={handleMobileMenuOpen}
                sx={{ display: { xs: 'flex', md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
              >
                <MenuItem onClick={() => handleNavigation('/install-gateway')}>
                  Install Gateway
                </MenuItem>
                <MenuItem onClick={() => handleNavigation('/subscription/plans')}>
                  Pricing
                </MenuItem>
                <MenuItem onClick={() => handleNavigation(loginHref)}>
                  Login
                </MenuItem>
                <MenuItem onClick={() => handleNavigation(registerHref)}>
                  Get Started
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          pt: 12, // Extra padding for fixed AppBar
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.6 }}>
              <Box
                component="img"
                src="/eyesense-logo-v2-tight.png"
                alt="EyeSense.ai Modern Logo"
                sx={{
                  width: { xs: 56, md: 74 },
                  height: 'auto',
                  display: 'block',
                }}
              />
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 'bold',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  margin: 0,
                  lineHeight: 1.05,
                  textAlign: 'center',
                }}
              >
                EyeSense.ai
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h5" color="text.secondary" paragraph sx={{ mb: 2 }}>
                Intelligent Edge AI for Camera Monitoring and Automated Response
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Invite codes are gone. Create an account — no code required.
              </Alert>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2" component="div" sx={{ lineHeight: 1.7 }}>
                  <strong>Deer detection does not need a GPU.</strong> The included model is a small fine-tuned YOLO model.
                  If you are not running a large vision model, that YOLO model fits a smaller computer, including a Raspberry Pi.
                  A GPU is only for checking those detections with a large vision model, or for fine-tuning your own small model so you can later move it back to the smaller computer.
                  The factory USB image is a separate, larger install: 16 GB of RAM and 80 GB of disk. With a GPU, plan on 24 GB and 128 GB.
                  Wildfire detection is in development.
                </Typography>
              </Alert>
              <Box
                component="a"
                href="https://pomsoft.net"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: { xs: 'block', md: 'none' },
                  float: 'right',
                  transition: 'opacity 0.3s ease-in-out',
                  opacity: 0.65,
                  '&:hover': {
                    opacity: 1
                  },
                  textDecoration: 'none',
                  width: '80px',
                  ml: 2,
                  mb: 2
                }}
              >
                <Box
                  component="img"
                  src="/powered_by_pomsoft.png"
                  alt="Powered by Pomsoft"
                  sx={{
                    height: 'auto',
                    width: '100%',
                    display: 'block'
                  }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 4 }}>
                EyeSense watches your cameras on hardware you install yourself.
                The deer model that ships is small, fine-tuned, and runs without a GPU.
                Add a GPU only when you want a large vision model to double-check those detections, or when you want to fine-tune a small model of your own.
                Once that small model is good enough, you can stop using the GPU.
                Wildfire detection is in development and is not in the model that ships today.
              </Typography>
              <Box sx={{ clear: 'both' }} />
              <Box sx={{ mb: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 'medium' }}>
                  Featured Use Cases:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip 
                    icon={<Pets />} 
                    label="Wildlife Protection" 
                    variant="outlined" 
                    sx={{ mb: 1 }}
                  />
                  <Chip 
                    icon={<Warning />} 
                    label="Wildfire (in development)" 
                    variant="outlined" 
                    sx={{ mb: 1 }}
                  />
                  <Chip 
                    icon={<Security />} 
                    label="Security Monitoring" 
                    variant="outlined" 
                    sx={{ mb: 1 }}
                  />
                  <Chip 
                    icon={<LocalFlorist />} 
                    label="Garden Protection" 
                    variant="outlined" 
                    sx={{ mb: 1 }}
                  />
                </Stack>
              </Box>
              <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                {isAuthenticated ? (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate('/dashboard')}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      Go to Dashboard
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      endIcon={<Launch />}
                      onClick={() => navigate('/gateways')}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      Manage Gateways
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate(registerHref)}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      Get Started
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      endIcon={<Launch />}
                      onClick={() => navigate(loginHref)}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      Login
                    </Button>
                  </>
                )}
              </Stack>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Chip icon={<CloudOff />} label="Offline Operation*" variant="outlined" />
                <Chip icon={<Shield />} label="Zero Trust Security" variant="outlined" />
                <Chip icon={<Speed />} label="Real-time AI" variant="outlined" />
                <Chip icon={<Memory />} label="High Performance" variant="outlined" />
                <Chip icon={<Psychology />} label="Adaptive ML Fine-tuning" variant="outlined" sx={{ mt: { xs: 1, md: 0 } }} />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontStyle: 'italic' }}>
                *Core AI processing operates offline. Some IoT integrations (Rachio, etc.) may require internet connectivity.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 3, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                  <Box
                    component="a"
                    href="https://pomsoft.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-block',
                      transition: 'opacity 0.3s ease-in-out',
                      opacity: 0.65,
                      '&:hover': {
                        opacity: 1
                      },
                      textDecoration: 'none'
                    }}
                  >
                    <Box
                      component="img"
                      src="/powered_by_pomsoft.png"
                      alt="Powered by Pomsoft"
                      sx={{
                        height: 'auto',
                        maxWidth: 160,
                        width: '100%',
                        display: 'block'
                      }}
                    />
                  </Box>
                </Box>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 3,
                    color: theme.palette.primary.main
                  }}
                >
                  🎥 EyeSense.ai in Action!
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 3, fontStyle: 'italic' }}
                >
                  Watch our AI system detect and successfully deter a deer from entering a protected area
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    height: 400,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: theme.shadows[10],
                    bgcolor: '#000'
                  }}
                >
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/e8pcbWcgYYA"
                    title="EyeSense.ai Deer Deterrent System in Action"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{
                      borderRadius: '8px'
                    }}
                  />
                </Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ mt: 2, display: 'block', fontWeight: 'medium' }}
                >
                  ✅ Real deployment • ✅ Live detection • ✅ Automatic response
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* System Architecture Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.grey[100], 0.5), py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 3 }}>
            How EyeSense.ai Works
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            color="text.secondary" 
            sx={{ mb: 6, maxWidth: '800px', mx: 'auto', lineHeight: 1.6 }}
          >
            Our edge AI gateway orchestrates your entire monitoring ecosystem, connecting cameras, 
            deterrents, and IoT devices for intelligent automated responses
          </Typography>
          
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <HomepageArt
                src="/homepage-architecture.png"
                alt="EyeSense gateway connecting cameras, sprinklers, and deterrents at the property edge"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                  Intelligent Edge Processing
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#3498db', mr: 2 }} />
                    1. Camera Input
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5, mb: 2 }}>
                    Multiple RTSP camera streams feed real-time video to the gateway
                  </Typography>
                </Box>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#3498db', mr: 2 }} />
                    2. AI Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5, mb: 2 }}>
                    High-performance edge AI processors analyze video frames for object detection
                  </Typography>
                </Box>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e74c3c', mr: 2 }} />
                    3. Automated Response
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5, mb: 2 }}>
                    Triggers sprinklers, sirens, and other deterrents instantly upon detection
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2ecc71', mr: 2 }} />
                    4. Secure Communication
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                    All processing happens locally with optional secure cloud connectivity
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Use Cases Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 6 }}>
          Powerful Use Cases
        </Typography>
        <Grid container spacing={4}>
          {useCases.map((useCase, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[10]
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {useCase.icon}
                    <Typography variant="h5" component="h3" sx={{ ml: 2, fontWeight: 'bold' }}>
                      {useCase.title}
                    </Typography>
                  </Box>
                  {useCase.status && (
                    <Chip label={useCase.status} size="small" color="warning" sx={{ mb: 1.5 }} />
                  )}
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {useCase.description}
                  </Typography>
                  <List dense>
                    {useCase.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Key Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 6 }}>
          Why Choose EyeSense.ai?
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  mb: 3
                }}
              >
                <CloudOff sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Privacy First
              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Your data never leaves your property. EyeSense.ai operates completely offline for core AI processing, 
                  ensuring your privacy and security while delivering real-time analysis. Our adaptive ML fine-tuning 
                  system works locally to continuously improve detection accuracy over time. Note that some IoT integrations 
                  like Rachio sprinkler systems may require internet connectivity for their own cloud services.
                </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  mb: 3
                }}
              >
                <Memory sx={{ fontSize: 40, color: theme.palette.secondary.main }} />
              </Box>
              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Edge AI Power
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                If you skip the large vision model, the included YOLO deer model fits a smaller computer, including a Raspberry Pi, and it does not need a GPU.
                A Jetson, Mac Mini, or GPU PC is the optional path: use it to check detections with a large vision model, or to fine-tune a small model, then move that model back to the smaller computer.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  mb: 3
                }}
              >
                <Speed sx={{ fontSize: 40, color: theme.palette.success.main }} />
              </Box>
              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Real-time Response
              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Timely detection and response ensure immediate action when it matters most. 
                  The shipped model is for deer. Wildfire detection is in development.
                  A large vision model can check the small model's detections when you have a GPU, and overnight fine-tuning can fold those lessons into a small model that no longer needs one.
                </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* The Learning AI Section - NEW FEATURED SECTION */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), py: 8, borderTop: `4px solid ${theme.palette.primary.main}`, borderBottom: `4px solid ${theme.palette.primary.main}` }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              The World's First Learning Camera System
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 3, maxWidth: 800, mx: 'auto' }}>
              Just like the revolutionary learning thermostat changed home climate control forever, 
              EyeSense.ai is transforming camera systems with AI that learns while you sleep
            </Typography>
          </Box>
          
          <Grid container spacing={6} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <HomepageArt
                src="/homepage-learning.png"
                alt="Night camera watching a meadow while a neural constellation learns overhead"
                shadow={10}
                aspectRatio="3 / 4"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                AI That Dreams & Learns Overnight
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 4, lineHeight: 1.8 }}>
                <strong>EyeSense.ai is the world's first truly learning camera system.</strong> Just as your brain processes 
                the day's experiences during sleep, our revolutionary Adaptive ML Fine-Tuning system works through the night, 
                transforming daily detections into permanent intelligence.
              </Typography>
              
              <Box sx={{ mb: 4, p: 3, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                  <Psychology sx={{ mr: 1, color: theme.palette.secondary.main }} /> 
                  The Master-Apprentice Learning Model
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  During the day, the small YOLO deer model watches the cameras. It does not need a GPU, and without a large vision model it fits a smaller computer.
                  When you want a second opinion, a large vision model checks that detection. That check needs a GPU.
                  Overnight fine-tuning, also on the GPU, turns those checks into a small model. When that model is good enough, you move it back to the smaller computer and stop using the GPU.
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary }}>
                  The model that ships today is deer detection. Wildfire detection is in development.
                  Large-vision checks are not a cloud add-on with a separate price. They run on a GPU you supply, or you skip them and run the small model alone.
                </Typography>
              </Box>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, height: '100%', bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.success.main, mb: 1 }}>
                      90%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reduction in false positives after just one week of learning
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, height: '100%', bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.info.main, mb: 1 }}>
                      5x
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Faster response time than systems using LLMs for all detections
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              <Button 
                variant="contained" 
                size="large" 
                color="secondary"
                endIcon={<ArrowForward />}
                sx={{ px: 4, py: 1.5 }}
                onClick={() => navigate(registerHref)}
              >
                Experience Learning AI
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Hardware Spotlight Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05), py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Two footprints, one deer model
              </Typography>
              <Typography variant="h6" color="text.secondary" paragraph>
                No GPU for the included deer model. A GPU only while you are checking or training.
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                The gateway ships with a small fine-tuned YOLO deer model. If you are not running a large vision model, that model fits a smaller computer, including a Raspberry Pi, and it does not need a GPU.
                If you have a GPU, you can use it two ways: a large vision model can validate the small model's detections, and you can fine-tune a small model of your own.
                When that small model is ready, move it back to the smaller computer and stop using the GPU.
                The 16 GB / 80 GB numbers are for the factory USB image, which also installs the desktop and hub. They are not the size of the YOLO model.
                The USB installer is about 9.2 GB. Flashing it erases the USB stick, and booting it erases the target computer's drive.
              </Typography>
              
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      Smaller
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      YOLO deer model, no large vision model
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                      Multiple Options
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hardware Flexibility
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                      Ultra Efficient
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Energy Consumption
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                      Fast
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Response Time
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3, fontStyle: 'italic' }}>
                You install the software yourself. The install guide lists the USB path and the shorter zip path for a computer that already has an operating system.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <HomepageArt
                src="/homepage-hardware.png"
                alt="Compact mini PC and single-board computer used as EyeSense gateways"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Multi-Model AI Technology Section - NEW SECTION */}
      <Box sx={{ bgcolor: alpha(theme.palette.info.main, 0.05), py: 8, borderTop: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 2 }}>
            Our Multi-Model AI Technology
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            EyeSense.ai combines multiple AI models in a unique two-stage detection system for maximum accuracy and efficiency
          </Typography>
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 4 }}>
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.info.main, mb: 3 }}>
                  How Our Two-Stage Detection Works
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: theme.palette.primary.main, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, fontWeight: 'bold' }}>1</Box>
                    Primary Detection with YOLO
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 7, mb: 3 }}>
                    Our lightweight, fine-tuned YOLO model continuously monitors camera feeds in real-time, 
                    efficiently detecting potential objects of interest with minimal power consumption.
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: theme.palette.secondary.main, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, fontWeight: 'bold' }}>2</Box>
                    LLM Validation
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 7, mb: 2 }}>
                    If you want a second opinion, a large vision model checks the detection. That step needs a GPU on the gateway. Skip it and the small deer model still runs.
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 7, fontStyle: 'italic', color: theme.palette.text.secondary }}>
                    This is what we call "LLM validation" - a critical step that ensures extremely high accuracy 
                    while maintaining the efficiency of our lightweight primary model.
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: theme.palette.success.main, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, fontWeight: 'bold' }}>3</Box>
                    Overnight Learning
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 7 }}>
                    Every false positive caught by the LLM becomes a learning opportunity. Our system collects 
                    these examples and uses them for overnight fine-tuning, continuously improving the primary 
                    YOLO model and reducing the need for LLM validation over time.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  bgcolor: 'background.paper',
                  boxShadow: theme.shadows[4],
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Multi-Model AI Architecture
                  </Typography>
                  <Chip label="Patent Pending" size="small" color="primary" />
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                      Primary Detection Model (YOLO)
                    </Typography>
                    <Typography variant="body2">
                      • Small fine-tuned YOLO deer model<br />
                      • No GPU, and no large vision model<br />
                      • Fits a smaller computer, including a Raspberry Pi<br />
                      • Fine-tuning is optional and uses a GPU
                    </Typography>
                  </Paper>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                    <Box sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ArrowForward sx={{ color: theme.palette.text.secondary }} />
                    </Box>
                  </Box>
                  
                  <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05), border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main, mb: 1 }}>
                      Secondary Validation (LLM)
                    </Typography>
                    <Typography variant="body2">
                      • Large vision model, for example Qwen2.5-VL<br />
                      • Checks the small model's detections<br />
                      • Needs a GPU: 24 GB RAM and 128 GB disk<br />
                      • Not a cloud add-on and not part of the $15 plan
                    </Typography>
                    <Box sx={{ mt: 2, p: 1, bgcolor: alpha(theme.palette.warning.light, 0.1), borderRadius: 1, border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}` }}>
                      <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block' }}>
                        <strong>No large vision model:</strong> the YOLO deer model fits a smaller computer.
                        <strong> With a GPU:</strong> use the check, and fine-tune, then move the small model back.
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Gateway Technology Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 2 }}>
            The EyeSense.ai Gateway
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
            Software you install on a computer you supply. Start with the included deer model and no GPU. Add a GPU only for large-vision checks or fine-tuning.
          </Typography>

          <Grid container spacing={4} sx={{ mb: 6 }}>
            {gatewayFeatures.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    height: '100%',
                    textAlign: 'center',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      mb: 2
                    }}
                  >
                    {React.cloneElement(feature.icon, { 
                      sx: { fontSize: 32, color: theme.palette.primary.main } 
                    })}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Technical Specifications */}
          <Paper sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h5" component="h3" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
              Supported Hardware
            </Typography>
            
            {/* Computing Platforms */}
            <Typography variant="h6" component="h4" gutterBottom sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
              Computing Platforms
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6} lg={3}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    YOLO only — smaller computer
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    The included deer model, with no large vision model and no GPU.
                    A Raspberry Pi class machine is enough for this model.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    GPU — check or fine-tune
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    24 GB of RAM and 128 GB of disk when the machine has an NVIDIA, AMD, or Apple GPU.
                    Use it to validate detections or fine-tune a small model, then move that model back to the smaller computer.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Mac with Apple silicon
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Apple Metal counts as a GPU for local large-vision checks and fine-tuning.
                    Size it like the 24 GB path if you load those models. It is optional for deer detection.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Windows or Linux with a GPU
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    NVIDIA or AMD, including WSL2. Same role as the other GPU machines: validate or fine-tune, then wean off the GPU.
                    The USB installer is for a UEFI PC and erases that PC's drive.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Camera Systems */}
            <Typography variant="h6" component="h4" gutterBottom sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
              Camera Systems
            </Typography>
            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Amcrest Dual-Lens 4MP AI Outdoor Security Camera (IP4M-T1044EW-AI)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Dual 2K AI lenses with superior low light performance (0.0002 lux @F1.0), 164ft color night vision, 
                115° combined viewing angle, active deterrents with 110db siren, built-in spotlight, 
                two-way audio, PoE powered, IP67 weatherproof housing
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Features: Human & vehicle detection, tripwire alerts, intrusion detection, perimeter protection
              </Typography>
            </Box>

            {/* IoT Integrations */}
            <Typography variant="h6" component="h4" gutterBottom sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}>
              IoT Integrations
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Rachio Smart Sprinkler Controller
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Automated irrigation control and zone-specific watering triggered by AI detections
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Orbit B-hyve WiFi Hose Faucet Timer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Smart hose control for targeted watering and deterrent activation
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    GPIO-Controlled Devices
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Direct hardware control for valves, sirens, lights, and custom deterrent systems
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Central Hub Management Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Centralized Management
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Monitor and manage multiple gateways from a single, secure hub
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
              The EyeSense.ai Central Hub provides enterprise-grade management for distributed gateway networks. 
              With zero-trust security, real-time monitoring, and seamless remote access, you can oversee 
              multiple locations from anywhere in the world.
            </Typography>
            <List>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <NetworkCheck sx={{ color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Real-time Gateway Monitoring" 
                  secondary="Monitor connection status, performance metrics, and health indicators"
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <Router sx={{ color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Dynamic Routing & Proxying" 
                  secondary="Seamless access to any connected gateway through secure tunnels"
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <Shield sx={{ color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Zero Trust Security Architecture" 
                  secondary="WireGuard VPN with outbound-only connections and JWT authentication"
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <HomepageArt
              src="/homepage-hub.png"
              alt="Central EyeSense hub linking gateways across four rural properties"
            />
          </Grid>
        </Grid>
      </Container>

      {/* EZGateway Feature Section */}
      <Box sx={{ 
        bgcolor: 'white', 
        py: 8, 
        borderTop: `4px solid ${theme.palette.primary.main}`,
        borderBottom: `4px solid ${theme.palette.primary.main}`
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip 
              label="🚀 NEW FEATURE" 
              color="primary" 
              sx={{ mb: 2, fontWeight: 'bold', fontSize: '0.9rem', px: 2, py: 0.5 }}
            />
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom
              sx={{
                fontWeight: 'bold',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              EZGateway™
            </Typography>
            <Typography variant="h4" color="text.secondary" gutterBottom>
              Zero-Touch Gateway Registration
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mt: 3, lineHeight: 1.8 }}>
              Five minutes is hub registration after EyeSense is already installed. A new computer uses the USB installer, which is about 9.2 GB and erases the target drive.
            </Typography>
          </Box>

          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%', bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.error.main, mb: 3 }}>
                  ❌ The Old Way (Manual Setup)
                </Typography>
                <List>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: theme.palette.error.main, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>1</Box>
                    </ListItemIcon>
                    <ListItemText primary="Manual WireGuard key generation" secondary="Run complex commands, copy/paste keys" />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: theme.palette.error.main, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>2</Box>
                    </ListItemIcon>
                    <ListItemText primary="SSH into hub server" secondary="Edit configuration files manually" />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: theme.palette.error.main, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>3</Box>
                    </ListItemIcon>
                    <ListItemText primary="Distribute config files" secondary="Copy WireGuard configs to gateway device" />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: theme.palette.error.main, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem' }}>4</Box>
                    </ListItemIcon>
                    <ListItemText primary="Restart hub VPN server" secondary="Disrupts ALL connected gateways" />
                  </ListItem>
                </List>
                <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                    30+ minutes
                  </Typography>
                  <Typography variant="body2">Per gateway deployment</Typography>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    ⚠️ Requires VPN expertise • Error-prone • Not scalable
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%', bgcolor: alpha(theme.palette.success.main, 0.05), position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', bgcolor: alpha(theme.palette.success.main, 0.1) }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.success.main, mb: 3 }}>
                  ✅ The EZGateway Way
                </Typography>
                <Box sx={{ mb: 3, p: 3, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    One Command. Zero Configuration.
                  </Typography>
                  <Box sx={{ fontFamily: 'monospace', bgcolor: '#000', color: '#0f0', p: 2, borderRadius: 1, fontSize: '0.9rem' }}>
                    ./prepare-for-docker.sh<br/>
                    # Answer 'Y' → Done! ✅
                  </Box>
                </Box>
                <List>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Automatic Hub Discovery" 
                      secondary="DNS SRV, environment variable, or direct URL"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Secure ECDH Handshake" 
                      secondary="Cryptographic authentication without pre-shared secrets"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Web-Based Approval" 
                      secondary="One-click approval in admin dashboard"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 1 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Zero Downtime" 
                      secondary="Live VPN peer addition - no hub restart"
                    />
                  </ListItem>
                </List>
                <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.success.main, 0.2), borderRadius: 1 }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                    5 minutes
                  </Typography>
                  <Typography variant="body2">Hub registration, after the computer is installed</Typography>
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}>
                    The USB install is a separate, longer step and erases the drive
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Perfect for Enterprise & SaaS Distribution
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                    245
                  </Typography>
                  <Typography variant="body2">Concurrent gateways supported</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 3, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main, mb: 1 }}>
                    100%
                  </Typography>
                  <Typography variant="body2">Zero-downtime operations</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.success.main, mb: 1 }}>
                    Secure
                  </Typography>
                  <Typography variant="body2">ECDH + challenge-response</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.info.main, mb: 1 }}>
                    3 Ways
                  </Typography>
                  <Typography variant="body2">Auto-discovery, URL, invitation</Typography>
                </Box>
              </Grid>
            </Grid>
            
            {!isAuthenticated && (
              <Box sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate(registerHref)}
                  sx={{ px: 6, py: 2, fontSize: '1.1rem' }}
                >
                  Get Started with EZGateway
                </Button>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* Gateway Installation Guide Section - NEW SECTION */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Ready to Deploy Your Gateway?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            The YOLO deer model fits a smaller computer when you are not running a large vision model. The install guide's 16 GB / 80 GB figures are for the factory USB image. A GPU is optional.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-8px)', boxShadow: theme.shadows[10] } }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <DeviceHub sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                    Installation Guide
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3, minHeight: 60 }}>
                  The USB installer, the zip install for a computer that already has an OS, and the split between a smaller YOLO-only computer and the larger factory image. A GPU path is documented separately.
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText primary="System requirements" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText primary="Hardware selection guide" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText primary="USB installer (erases the drive) or zip install on an existing OS" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText primary="Common issues & solutions" />
                  </ListItem>
                </List>
              </CardContent>
              <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/install-gateway')}
                >
                  View Installation Guide
                </Button>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-8px)', boxShadow: theme.shadows[10] } }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Launch sx={{ fontSize: 40, color: theme.palette.secondary.main, mr: 2 }} />
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                    What You'll Get
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3, minHeight: 60 }}>
                  The package includes the gateway software and the deer model. You still install it yourself, then register the gateway with the hub.
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Download sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                    </ListItemIcon>
                    <ListItemText primary="Pre-compiled backend JAR" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Download sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                    </ListItemIcon>
                    <ListItemText primary="Pre-built React frontend" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Download sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                    </ListItemIcon>
                    <ListItemText primary="AI models & synsets" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Download sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                    </ListItemIcon>
                    <ListItemText primary="Automatic central hub registration" />
                  </ListItem>
                </List>
              </CardContent>
              <Box sx={{ p: 2, bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                <Alert severity="info" sx={{ p: 1 }}>
                  <Typography variant="caption">
                    <strong>Ready to deploy?</strong> Download the gateway package and run setup.sh on your hardware.
                  </Typography>
                </Alert>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Pricing Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
              Simple, Transparent Pricing
            </Typography>
            <Typography variant="h6" color="text.secondary">
              $15 per gateway per month after a 30-day trial. Checkout collects a card. You are not charged during the trial.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={6} lg={5}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: '2px solid',
                  borderColor: theme.palette.primary.main,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[10]
                  }
                }}
              >
                <Chip
                  label="RECOMMENDED"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                  }}
                />
                <CardContent sx={{ flexGrow: 1, pt: 6 }}>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Gateway Access
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3 }}>
                    <Typography variant="h2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                      $15
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                      /month
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Perfect for single gateway deployment
                  </Typography>

                  <List>
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary="30-day trial. A card is collected and not charged until the trial ends." />
                    </ListItem>
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary="Single gateway access" />
                    </ListItem>
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary="Local AI processing" />
                    </ListItem>
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary="Secure VPN connection" />
                    </ListItem>
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary="Real-time alerts" />
                    </ListItem>
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText primary="Web dashboard access" />
                    </ListItem>
                  </List>

                  <Box sx={{ mt: 4, p: 2, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Cancel before day 30 and you are not charged. Otherwise the plan continues at $15 per gateway per month. Large-vision checks need a GPU you supply. They are not an extra line item on this plan.
                    </Typography>
                  </Box>
                </CardContent>
                <Box sx={{ p: 3 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/subscription/plans')}
                  >
                    See pricing
                  </Button>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box
        sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: 8,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ready to Deploy EyeSense.ai?
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: 4, opacity: 0.9 }}>
            Join the future of intelligent edge computing. Deploy EyeSense.ai at your location 
            and experience the power of autonomous monitoring and response.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            {isAuthenticated ? (
              <>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.9)
                    }
                  }}
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: alpha('#ffffff', 0.1)
                    }
                  }}
                  onClick={() => navigate('/gateways')}
                >
                  Manage Gateways
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: alpha('#ffffff', 0.9)
                    }
                  }}
                  onClick={() => navigate(registerHref)}
                >
                  Start Your Hub
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: alpha('#ffffff', 0.1)
                    }
                  }}
                  onClick={() => navigate(loginHref)}
                >
                  Login
                </Button>
              </>
            )}
          </Stack>
        </Container>
      </Box>
      <PublicFooter />
    </Box>
  );
};

export default Homepage;
